import React, { useCallback, useEffect, useRef } from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import { rem } from 'polished'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { forgotPassword, login } from 'actions/AuthActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import TextLink from 'components/Luxkit/TextLink'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { signinModalOpen, userForgotPassword } from 'analytics/eventDefinitions'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { isLoggedIn } from 'selectors/accountSelectors'
import { AccountAccessModalResult } from './AccountAccessModal'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'

const Form = styled(VerticalSpacer)`
  padding-bottom: ${rem(320)};
`

interface LoginForm {
  email: string;
  password: string;
}

interface Props {
  error?: AnyStyledComponent;
  email: string;
  isStoreMode: boolean;
  onModeChange?: (mode: App.UiAccountModalMode) => void;
}

function AccountAccessLoginPassword(props: Props) {
  const {
    error,
    email,
    isStoreMode,
    onModeChange = noop,
  } = props

  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const loggedIn = useAppSelector(isLoggedIn)

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  useEffect(() => {
    Analytics.track('client-event', clientEvent('sign_in_password', 'impression', 'logging', 'operational'))
    nativeInputRef.current?.focus()
    fireNonInteractionEvent(signinModalOpen())
  }, [])

  const dispatch = useAppDispatch()

  const reRef = useRef<ReCAPTCHA>(null)

  const onForgotPassword = () => {
    dispatch(forgotPassword(email))
    fireInteractionEvent(userForgotPassword())
    onModeChange('forgotPassword')
  }

  const onLogin = async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const loginData = formToObject<LoginForm>(e.currentTarget)
    let recaptchaData
    if (reRef.current) {
      recaptchaData = await reRef.current.executeAsync()
    }
    dispatch(login(loginData.email, loginData.password, recaptchaData))
  }

  const onBack = useCallback(() => onModeChange('login'), [onModeChange])

  return (<>
    <ModalHeader
      title="Welcome back"
      onBackButtonClick={onBack}
    />
    <ModalBody>
      <ModalContent>
        <Form gap={24} as="form" onSubmit={onLogin} name="loginForm">
          <Group direction="vertical" gap={20}>
            {error?.message && <MessageBanner kind="critical" description={error.message} />}
          </Group>
          <BodyText variant="medium" >Please enter your password below to continue.</BodyText>
          <fieldset>
            <HiddenInput
              id="email"
              name="email"
              value={email}
            />
            <PasswordInput
              label="Password"
              id="password"
              required
              name="password"
              placeholder="Password"
              requiredErrorMessage="Password is required"
              ref={nativeInputRef}
            />
            <Group direction="vertical" gap={16} horizontalAlign="center">
              <TextButton
                kind="primary"
                size="large"
                type="submit"
                fit="flex">
                Log in
              </TextButton>
              {!isStoreMode && <TextLink
                onClick={onForgotPassword}
                size="medium"
              >
                Forgot your password?
              </TextLink>}
            </Group>
            {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
          </fieldset>
        </Form>
      </ModalContent>
    </ModalBody>
  </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    isStoreMode: state.config.storeMode,
  }
}

export default connect(mapStateToProps)(AccountAccessLoginPassword)
